import Joi from "joi";
import PropTypes from "prop-types";
import _ from "lodash";

import Button from "../../themed/button";
import Conditional from "../../common/conditional";
import Form from "../../common/form";

import { getFileUploadTypes } from "../../../utils/utilities";
import { getAnomalyTypeClassOptions } from "../../../utils/data";
import { CoverDataItem, PDFImageProps } from "../../../utils/models";

import toastService from "../../../services/toastService";

interface AnalysisItem {
    cdrNumber: string;
    description: CoverDataItem[];
    recommendation: CoverDataItem[];
    generalImage: PDFImageProps[];
    closeImage: PDFImageProps[];
}

const selectionSchema = Joi.object({
    label: Joi.string().required(),
    value: Joi.string().required(),
    __isNew__: Joi.boolean().optional(),
});
 
class ThreatAnalysisForm extends Form {
    state = {
		data: {
			cdrNumber: '',
			description: [],
			recommendation: [],
			generalImage: [],
			closeImage: [],
		},
		errors: {},
		loading: false,
		progress: 0,
	};

    schema = {
            id: Joi.number().optional().label('S/N'),
            cdrNumber: Joi.string().required().label('CDR Number'),
            description: Joi.array()
                .min(1)
                .items(selectionSchema)
                .required()
                .label('Inspection Description'),
            recommendation: Joi.array()
                .min(1)
                .items(selectionSchema)
                .required()
                .label('Remark & Recommendation'),
            generalImage: Joi.array().min(1).max(1).required().label('General Image'),
            closeImage: Joi.array().min(1).max(1).required().label('Close Image'),
    };

    static propTypes = {
        onSave: PropTypes.func,
        analysisItem: PropTypes.object,
        onDelete: PropTypes.func,
    };

	componentDidMount(): void {
		if (this.props.analysisItem) {
			this.setState({ data: this.props.analysisItem });
		}
	}

    getSelectedClassRecommendations = () => {
        const recommendations = getAnomalyTypeClassOptions().flatMap((anomaly) => anomaly.recommendations);
        return _.uniqBy(recommendations, (recommendation) => recommendation.value);
    };
   
    getSelectedClassDescriptions = () => {
        const descriptions = getAnomalyTypeClassOptions().flatMap((anomaly) => anomaly.descriptions);
        return _.uniqBy(descriptions, (description) => description.value);
    };

    doSubmit = (): void => {
		this.props.onSave(this.state.data);

        if (!this.props.analysisItem) {
            this.setState({ 
                data: {
                    cdrNumber: '',
                    description: [],
                    recommendation: [],
                    generalImage: [],
                    closeImage: [],
                },
            });
        }

		toastService.success('Analysis Item Saved Successfully!');
	};

    render() { 
        return ( 
            <form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderInput({
							name: 'cdrNumber',
							placeholder: 'Enter CDR Number',
							label: 'CDR Number',
						})}
					</div>

					<div className="col">
						{this.renderCreatableMultiselect({
							closeMenuOnSelect: true,
							options: this.getSelectedClassDescriptions(),
							name: 'description',
							placeholder: 'Enter Threat Description',
							label: 'Threat Description',
							isMulti: true,
						})}
					</div>

                    <div className="col">
						{this.renderCreatableMultiselect({
							closeMenuOnSelect: true,
							options: this.getSelectedClassRecommendations(),
							name: 'recommendation',
							placeholder: 'Enter Remark & Recommendation',
							label: 'Remark & Recommendation',
							isMulti: true,
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderFileInput({
							max: 1,
							multiple: false,
							hideCaption: true,
							name: 'generalImage',
							accept: 'General Image View',
							sublabel: 'Upload photos showing general view',
							types: getFileUploadTypes(),
						})}
					</div>

					<div className="col">
						{this.renderFileInput({
							max: 1,
							multiple: false,
							hideCaption: true,
							name: 'closeImage',
							accept: 'image/*,application/pdf',
							label: 'Close Image View',
							sublabel: 'Upload photos showing close view',
							types: getFileUploadTypes(),
						})}
					</div>
				</div>

				<div className="divider mt-4"></div>

				<div className="d-flex justify-content-end">
					{this.renderButton('Save Analysis Item')}

					<Conditional isVisible={!!this.props.onDelete}>
						<div className="mx-4">
							<Button
								label="Delete"
								style={{ backgroundColor: 'red' }}
								onClick={this.props.onDelete}
							/>
						</div>
					</Conditional>
				</div>
			</form>
        );
    }
}

export type { AnalysisItem };
export default ThreatAnalysisForm;